export type QuoteStyle = {
  readonly useCurlyDouble: boolean;
  readonly useCurlySingle: boolean;
};

/**
 * Detects the preferred quote style in a text by counting occurrences.
 * Uses a majority-wins approach: if curly quotes outnumber straight quotes,
 * the text is considered to use curly quotes.
 *
 * @param text - The text to analyze for quote style
 * @returns QuoteStyle indicating which quote styles are preferred
 *
 * @example
 * ```typescript
 * detectQuoteStyle('text with curly quotes'); // { useCurlyDouble: true, useCurlySingle: false }
 * detectQuoteStyle('"straight" quotes');      // { useCurlyDouble: false, useCurlySingle: false }
 * ```
 */
export function detectQuoteStyle(text: string): QuoteStyle {
  // Count curly double quotes (\u201C and \u201D).
  const curlyDoubleCount = (text.match(/[\u201C\u201D]/g) ?? []).length;
  // Count straight double quotes (").
  const straightDoubleCount = (text.match(/"/g) ?? []).length;
  // Count curly single quotes/apostrophes (\u2018 and \u2019).
  const curlySingleCount = (text.match(/[\u2018\u2019]/g) ?? []).length;
  // Count straight single quotes/apostrophes (').
  const straightSingleCount = (text.match(/'/g) ?? []).length;

  return {
    useCurlyDouble: curlyDoubleCount > straightDoubleCount,
    useCurlySingle: curlySingleCount > straightSingleCount,
  };
}

/**
 * Normalizes text to match the specified quote style.
 * Converts straight quotes to curly quotes if the style indicates curly preference.
 *
 * @param text - The text to normalize
 * @param style - The target quote style to match
 * @returns Normalized text with quotes converted to match the style
 *
 * @example
 * ```typescript
 * const style = { useCurlyDouble: true, useCurlySingle: true };
 * normalizeToQuoteStyle('"straight" and \'apostrophe\'', style);
 * // Returns: text with curly double quotes and curly apostrophe
 * ```
 */
export function normalizeToQuoteStyle(text: string, style: QuoteStyle): string {
  const withDoubleQuotes = style.useCurlyDouble ? convertStraightToCurlyDouble(text) : text;
  const withAllQuotes = style.useCurlySingle
    ? convertStraightToCurlySingle(withDoubleQuotes)
    : withDoubleQuotes;

  return withAllQuotes;
}

/**
 * Converts straight double quotes to curly double quotes using a toggle approach.
 * Alternates between left (\u201C) and right (\u201D) curly quotes on each occurrence.
 *
 * This approach works well for well-formed text where quotes are balanced.
 * It's simpler and more predictable than context-aware approaches.
 *
 * @param text - Text with straight double quotes
 * @returns Text with curly double quotes
 *
 * @example
 * ```typescript
 * convertStraightToCurlyDouble('He said "hello" to her');
 * // Returns: He said "hello" to her (with curly quotes)
 * ```
 */
function convertStraightToCurlyDouble(text: string): string {
  const result = text.split('').reduce<{ inQuote: boolean; output: string }>(
    (acc, char) => {
      if (char === '"') {
        const nextInQuote = !acc.inQuote;
        // Left when entering, right when exiting...
        const curlyQuote = nextInQuote ? '\u201C' : '\u201D';
        return {
          inQuote: nextInQuote,
          output: acc.output + curlyQuote,
        };
      }
      return {
        inQuote: acc.inQuote,
        output: acc.output + char,
      };
    },
    { inQuote: false, output: '' }
  );

  return result.output;
}

/**
 * Converts straight single quotes/apostrophes to curly single quotes.
 * Uses right curly single quote (\u2019) for all occurrences, which works well
 * for apostrophes and most single-quoted text.
 *
 * @param text - Text with straight single quotes
 * @returns Text with curly single quotes
 *
 * @example
 * ```typescript
 * convertStraightToCurlySingle("It's a 'quoted' word");
 * // Returns: It's a 'quoted' word
 * ```
 */
function convertStraightToCurlySingle(text: string): string {
  // Use right curly single quote (\u2019) for all (works for apostrophes and quotes)
  return text.replace(/'/g, '\u2019');
}
